import React, {
  Children,
  cloneElement,
  useRef,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Collapse from '../Collapse';
import { ChevronDown } from '../Icons';
import Accordion from '../Accordion/accordion';

const StyledItem = styled.div`
  border: 2px solid ${(props) => props.theme.colors.grayscale.gray4};
  display: block;
  position: relative;
  width: 100%;
`;

const StyledTitle = styled.button`
  ${(props) => props.theme.typography.headline};
  align-items: center;
  background-color: ${(props) => props.theme.colors.grayscale.white};
  cursor: pointer;
  border: 0;
  display: flex;
  margin: 0;
  min-height: 3rem;
  padding: 0 2rem;
  text-align: left;
  width: 100%;

  /* let the text fill the empty area */
  & span {
    display: inline-block;
    flex-grow: 1;
    margin-right: 1rem;
    overflow-x: hidden;
  }

  &:hover {
    color: ${(props) => props.theme.colors.grayscale.gray0};
  }
`;

const StyledChevron = styled(ChevronDown)`
  flex-shrink: 0;
  transform: ${(props) =>
    props['data-expanded'] ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease-in-out;
`;

const StyledContent = styled.div`
  background-color: ${(props) => props.theme.colors.grayscale.white};
  box-sizing: border-box;
  overflow-x: hidden;
  padding: 1rem 2rem;
`;

/**
 * AccordionItem is used in conjunction with the Accordion Component. However, it CAN be
 * used as a standalone in cases where you need single collapsible elements.  It uses the
 * Collapse component to create a collapsible element.
 */
function AccordionItem({ children, expanded, focused, id, title, ...props }) {
  const refButton = useRef(null);
  const refContent = useRef(null);
  const [isExpanded, _setExpanded] = useState(expanded);
  const expandedRef = useRef(isExpanded);

  const setExpanded = (p) => {
    expandedRef.current = p;
    _setExpanded(p);
  };

  /**
   * sets or un-sets the ability to focus on focusable content within an accordion item
   * depending on if the content is expanded or not.
   */
  const setInnerFocus = () => {
    const { current } = refContent;
    const focusable = current.querySelectorAll(`
      button, [href], input, select, textarea,
      [tabindex]:not([tabindex="-1"])
    `);

    // loop through each focusable item and depending on the expanded state set
    // the appropriate tabindex and aria hidden.
    for (let i = 0; i < focusable.length; i += 1) {
      focusable[i].setAttribute('tabindex', expandedRef.current ? '0' : '-1');
      focusable[i].setAttribute('aria-hidden', !expandedRef.current);
    }
  };

  // on update we want to disable any tabbable elements in a container
  // that is not expanded and update the expanded state.
  useEffect(() => {
    setExpanded(expanded);
    setInnerFocus();
  }, [expanded]);

  /**
   * Default onToggle handler.  Accordion passes in its own onToggle handler for
   * managing multiple accordion items.
   *
   * @param {string} idToToggle id of item to toggle.
   */
  const onToggle = (idToToggle) => {
    refButton.current.focus();

    if (typeof props.onToggle === 'function') {
      props.onToggle(idToToggle);
      return;
    }

    setExpanded(!isExpanded);
    setInnerFocus();
  };

  return (
    <StyledItem {...props}>
      <StyledTitle
        ref={refButton}
        type="button"
        id={id}
        onClick={() => onToggle(id)}
      >
        <span>{title}</span>
        <StyledChevron data-expanded={isExpanded} />
      </StyledTitle>
      <Collapse
        isOpened={isExpanded}
        aria-expanded={isExpanded}
        aria-controls={id}
        aria-hidden={!isExpanded}
        id={id}
      >
        <StyledContent
          id={`${id}-content`}
          ref={refContent}
          aria-hidden={!isExpanded}
        >
          {Children.toArray(children).map((child) => cloneElement(child, {}))}
        </StyledContent>
      </Collapse>
    </StyledItem>
  );
}

AccordionItem.displayName = 'AccordionItem';

AccordionItem.propTypes = {
  /** The content of the accordion item */
  children: PropTypes.node.isRequired,
  /** If the accordion item should be expanded by default */
  expanded: PropTypes.bool,
  /** If the accordion item should immediately gain focus.
   *  If there are multiple items with focused = true, focus will shift from first
   *  to last as each component mounts. You may want to avoid this.
   */
  focused: PropTypes.bool,
  /** The title of the accordion item (button text) */
  title: PropTypes.string.isRequired,
  /** A custom onToggle handler (for example see Accordion) */
  onToggle: PropTypes.func,
  /** The id of the item to expand/collapse (Also applied as an id attribute) */
  id: PropTypes.string.isRequired,
};

AccordionItem.defaultProps = {
  expanded: false,
  focused: false,
  onToggle: Accordion.onToggle,
};

/* @component */
export default AccordionItem;
